/** Shared query builders for `/vms/onboarding/*` flows (vendor hub + legacy annexures/dpia/contracts). */

import { getDpiaPartForCode } from './dpiaPartModel';
import type { DpiaPartKey } from './dpiaPartModel';

export const ONBOARDING_RETURN_TO_QUERY = 'returnTo';

/** Safe in-app return path (register detail, etc.). */
export function parseOnboardingReturnTo(raw: string | null | undefined): string | null {
  const v = String(raw ?? '').trim();
  if (!v.startsWith('/vms/') || v.includes('//')) return null;
  return v;
}

/** Preserve registry / register return path across onboarding sub-routes. */
export function withOnboardingReturnTo(href: string, returnTo: string | null | undefined): string {
  const safeReturn = parseOnboardingReturnTo(returnTo);
  if (!safeReturn) return href;
  const join = href.includes('?') ? '&' : '?';
  return `${href}${join}${ONBOARDING_RETURN_TO_QUERY}=${encodeURIComponent(safeReturn)}`;
}

export function annexureFillHref(setupHref: string, code: string): string {
  const join = setupHref.includes('?') ? '&' : '?';
  return `${setupHref}${join}annexure=${encodeURIComponent(code)}`;
}

export function buildOnboardingSetupHref(
  selectedRegId: number | null,
  vendorInitiationIdFromQuery: string | null,
): string {
  const path = '/vms/onboarding/annexures';
  const sp = new URLSearchParams();
  if (selectedRegId != null) sp.set('vms_registered_vendor_id', String(selectedRegId));
  if (vendorInitiationIdFromQuery && /^\d+$/.test(vendorInitiationIdFromQuery)) {
    sp.set('vendor_initiation_id', vendorInitiationIdFromQuery);
  }
  const q = sp.toString();
  return q ? `${path}?${q}` : path;
}

function onboardingQueryString(viId: number | null, registeredVendorId: number | null): string {
  const sp = new URLSearchParams();
  if (registeredVendorId != null) sp.set('vms_registered_vendor_id', String(registeredVendorId));
  if (viId != null && viId > 0) sp.set('vendor_initiation_id', String(viId));
  const q = sp.toString();
  return q ? `?${q}` : '';
}

export function buildOnboardingDpiaHref(viId: number | null, registeredVendorId: number | null): string {
  return `/vms/onboarding/dpia${onboardingQueryString(viId, registeredVendorId)}`;
}

export function buildOnboardingDpiaSetupHref(
  selectedRegId: number | null,
  vendorInitiationIdFromQuery: string | null,
): string {
  const path = '/vms/onboarding/dpia';
  const sp = new URLSearchParams();
  if (selectedRegId != null) sp.set('vms_registered_vendor_id', String(selectedRegId));
  if (vendorInitiationIdFromQuery && /^\d+$/.test(vendorInitiationIdFromQuery)) {
    sp.set('vendor_initiation_id', vendorInitiationIdFromQuery);
  }
  const q = sp.toString();
  return q ? `${path}?${q}` : path;
}

/** Opens the unified Part A/B scrollable form (section card grid removed). */
export function dpiaPartHref(setupHref: string, part: 'a' | 'b', section?: string): string {
  return dpiaPartFillHref(setupHref, part, section);
}

export function dpiaPartFillHref(setupHref: string, part: 'a' | 'b', section?: string): string {
  const join = setupHref.includes('?') ? '&' : '?';
  const base = `${setupHref}${join}part=${part}&fill=1`;
  if (!section) return base;
  return `${base}&section=${encodeURIComponent(section)}`;
}

/** @deprecated Per-section fill — use dpiaPartFillHref for unified Part A/B forms. */
export function dpiaFillHref(setupHref: string, code: string): string {
  const join = setupHref.includes('?') ? '&' : '?';
  return `${setupHref}${join}dpia=${encodeURIComponent(code)}`;
}

export function buildOnboardingDpiaPartAFillHref(
  selectedRegId: number | null,
  vendorInitiationIdFromQuery: string | null,
  returnTo?: string | null,
): string {
  return buildOnboardingDpiaPartFillHref(selectedRegId, vendorInitiationIdFromQuery, 'a', returnTo);
}

export function buildOnboardingDpiaPartFillHref(
  selectedRegId: number | null,
  vendorInitiationIdFromQuery: string | null,
  partOrDpiaCode: DpiaPartKey | string,
  returnTo?: string | null,
): string {
  const part: DpiaPartKey =
    partOrDpiaCode === 'a' || partOrDpiaCode === 'b'
      ? partOrDpiaCode
      : getDpiaPartForCode(partOrDpiaCode) ?? 'a';
  const setup = buildOnboardingDpiaSetupHref(selectedRegId, vendorInitiationIdFromQuery);
  let href = dpiaPartFillHref(setup, part);
  const safeReturn = parseOnboardingReturnTo(returnTo);
  if (safeReturn) {
    const join = href.includes('?') ? '&' : '?';
    href = `${href}${join}${ONBOARDING_RETURN_TO_QUERY}=${encodeURIComponent(safeReturn)}`;
  }
  return href;
}

export function buildOnboardingContractsHref(
  viId: number | null,
  registeredVendorId: number | null,
  returnTo?: string | null,
): string {
  const sp = new URLSearchParams();
  if (registeredVendorId != null) sp.set('vms_registered_vendor_id', String(registeredVendorId));
  if (viId != null && viId > 0) sp.set('vendor_initiation_id', String(viId));
  const safeReturn = parseOnboardingReturnTo(returnTo);
  if (safeReturn) sp.set(ONBOARDING_RETURN_TO_QUERY, safeReturn);
  const q = sp.toString();
  return q ? `/vms/onboarding/contracts?${q}` : '/vms/onboarding/contracts';
}

/** When true, contract review uses list + edit/view management UI; when false, legacy workspace on `/review`. */
export const ONBOARDING_CONTRACT_MANAGEMENT_UI_ENABLED = false;

export function onboardingContractVendorIdLabel(regId: number): string {
  return `VND-${String(regId).padStart(3, '0')}`;
}

/** Where contract approvers land when leaving review or with no vendor selected (legacy: My Tray). */
export function buildOnboardingContractApproverHubHref(): string {
  if (!ONBOARDING_CONTRACT_MANAGEMENT_UI_ENABLED) {
    return '/vms/my-tray';
  }
  return '/vms/onboarding/contracts';
}

export function buildOnboardingContractReviewListHref(registeredVendorId?: number | null): string {
  if (!ONBOARDING_CONTRACT_MANAGEMENT_UI_ENABLED) {
    if (registeredVendorId != null && registeredVendorId > 0) {
      return buildOnboardingContractReviewWorkspaceHref(registeredVendorId);
    }
    return buildOnboardingContractApproverHubHref();
  }
  if (registeredVendorId != null && registeredVendorId > 0) {
    return `/vms/onboarding/contracts/review?vms_registered_vendor_id=${registeredVendorId}`;
  }
  return '/vms/onboarding/contracts/review';
}

export function buildOnboardingContractReviewEditHref(registeredVendorId: number): string {
  return `/vms/onboarding/contracts/review/edit?vms_registered_vendor_id=${registeredVendorId}`;
}

export function buildOnboardingContractReviewViewHref(registeredVendorId: number): string {
  return `/vms/onboarding/contracts/review/view?vms_registered_vendor_id=${registeredVendorId}`;
}

/** Unified contract review workspace (approver actions, contract forms). */
export function buildOnboardingContractReviewWorkspaceHref(
  registeredVendorId: number,
  viId?: number | null,
): string {
  const sp = new URLSearchParams();
  sp.set('vms_registered_vendor_id', String(registeredVendorId));
  if (viId != null && viId > 0) sp.set('vendor_initiation_id', String(viId));
  const query = sp.toString();
  if (!ONBOARDING_CONTRACT_MANAGEMENT_UI_ENABLED) {
    return `/vms/onboarding/contracts/review?${query}`;
  }
  return `/vms/onboarding/contracts/review/workspace?${query}`;
}

/** Contract pipeline / hub link — approvers open review workspace directly in legacy flow. */
export function buildOnboardingContractEntryHref(
  viId: number | null,
  registeredVendorId: number,
  isContractApprover: boolean,
): string {
  if (isContractApprover && !ONBOARDING_CONTRACT_MANAGEMENT_UI_ENABLED) {
    return buildOnboardingContractReviewWorkspaceHref(registeredVendorId, viId);
  }
  return buildOnboardingContractsHref(viId, registeredVendorId);
}

export function buildOnboardingVendorPipelineHref(
  registeredVendorId: number,
  viId: number | null,
  returnTo?: string | null,
): string {
  const sp = new URLSearchParams();
  if (viId != null && viId > 0) sp.set('vendor_initiation_id', String(viId));
  const safeReturn = parseOnboardingReturnTo(returnTo);
  if (safeReturn) sp.set(ONBOARDING_RETURN_TO_QUERY, safeReturn);
  const q = sp.toString();
  return q
    ? `/vms/onboarding/vendors/${registeredVendorId}?${q}`
    : `/vms/onboarding/vendors/${registeredVendorId}`;
}

export function buildOnboardingNotificationRegulatorHref(
  registeredVendorId: number,
  viId: number | null,
  options?: { fromMyTray?: boolean },
): string {
  const sp = new URLSearchParams();
  if (viId != null && viId > 0) sp.set('vendor_initiation_id', String(viId));
  if (options?.fromMyTray) sp.set('from_my_tray', '1');
  const q = sp.toString();
  return q
    ? `/vms/onboarding/vendors/${registeredVendorId}/notification-regulator?${q}`
    : `/vms/onboarding/vendors/${registeredVendorId}/notification-regulator`;
}

export function buildOnboardingRegisterFillingHref(
  registeredVendorId: number,
  viId: number | null,
  options?: { fromMyTray?: boolean },
): string {
  const sp = new URLSearchParams();
  if (viId != null && viId > 0) sp.set('vendor_initiation_id', String(viId));
  if (options?.fromMyTray) sp.set('from_my_tray', '1');
  const q = sp.toString();
  return q
    ? `/vms/onboarding/vendors/${registeredVendorId}/register-filling?${q}`
    : `/vms/onboarding/vendors/${registeredVendorId}/register-filling`;
}

export function buildOnboardingAnnexureFillHref(
  viId: number | null,
  registeredVendorId: number,
  annexureCode: string,
): string {
  const setup = buildOnboardingSetupHref(registeredVendorId, viId != null ? String(viId) : null);
  return annexureFillHref(setup, annexureCode);
}

export function buildOngoingPeriodicReviewHref(params: {
  batchId?: number | null;
  outsourcingVendorId?: number | null;
  registeredVendorId?: number | null;
}): string {
  const sp = new URLSearchParams();
  sp.set('assessment', 'periodic');
  if (params.outsourcingVendorId != null && params.outsourcingVendorId > 0) {
    sp.set('vms_outsourcing_vendor_id', String(params.outsourcingVendorId));
  }
  if (params.registeredVendorId != null && params.registeredVendorId > 0) {
    sp.set('vms_registered_vendor_id', String(params.registeredVendorId));
  }
  if (params.batchId != null && params.batchId > 0) {
    sp.set('ongoing_batch_id', String(params.batchId));
  }
  return `/vms/ongoing?${sp.toString()}`;
}

